/* CommonNibApiMixin.js */

"use strict";
const BaseNibApiAdapter = require("./BaseNibApiMixin");
const JNIBError = require("./JnibError");
const mix = require("./util/mix");
const _ = require("lodash");

let mixin = (superKlass) =>
    class CommonNibApiMixin extends mix(superKlass).with(BaseNibApiAdapter) {
        //Note:
        //This "sanitization" is required because the LAB API struggles with urls like (http://localhost:57685/:443/nibAPI/getExistingRefs//org/instance/construct/)
        _sanitizeRef(ref) {
            return ref.replace(/^\//, "");
        }
        /**
         * This method is called with the /<path>/<name>._jsnib reference to load it
         * in the Browsers JavaScript engine.
         * The ref is always a absolute reference starting with '/'.
         */
        pmLoadJSNIB(continuation, ref) {
            return this.pmRequire(continuation, "/jsnib" + ref);
        }

        /**
         * This method is called with loadRef() call information to return
         * the completely resolved JSON.
         * The ref is always a absolute reference starting with '/'.
         */
        pmLoadRef(continuation, ref) {
            const errorObj = new JNIBError();

            return Promise.resolve()
                .then(() => {
                    return this._nibApiService.loadRef({
                        ref: this._sanitizeRef(ref),
                        orgId: this._userSession.activeOrganization.id,
                    });
                })
                .catch((error) => {
                    this._updateRefErrorObj(ref, error, errorObj);
                    throw errorObj;
                });
        }

        /**
         * This method is called with loadRefUnresolved() call information to return
         * just the unresolved JSON object.
         * The ref is always a absolute reference starting with '/'.
         */
        pmLoadRefUnresolved(continuation, ref) {
            const errorObj = new JNIBError();

            return Promise.resolve()
                .then(() => {
                    return this._nibApiService.loadRefUnresolved({
                        ref: this._sanitizeRef(ref),
                        orgId: this._userSession.activeOrganization.id,
                    });
                })
                .catch((error) => {
                    this._updateRefErrorObj(ref, error, errorObj);
                    throw errorObj;
                });
        }

        pmStorageFindMatches(continuation, data, ref) {
            const errorObj = new JNIBError();
            const requestObj = _.merge({}, data, {
                ref: this._sanitizeRef(ref),
                orgId: this._userSession.activeOrganization.id,
            });
            return Promise.resolve()
                .then(() => {
                    return this._nibApiService.storageFindDataRefs(requestObj);
                })
                .then((response) => {
                    //Note the JNIBEngineAPI is expecting the response to be a string so we get to convert this back into a string
                    if (_.isPlainObject(response)) {
                        return JSON.stringify(response);
                    } else if (_.isArray(response)) {
                        return JSON.stringify(response);
                    } else {
                        return response;
                    }
                })
                .catch((error) => {
                    this._updateRefErrorObj(ref, error, errorObj);
                    throw errorObj;
                });
        }

        /**
         * This method is called with doesRefExist() call information to return
         * a true if the reference exists, otherwise false.
         * The ref is always a absolute reference starting with '/'.
         */
        pmDoesRefExist(continuation, ref) {
            const errorObj = new JNIBError();

            return Promise.resolve()
                .then(() => {
                    return this._nibApiService.doesRefExist({
                        ref: this._sanitizeRef(ref),
                        orgId: this._userSession.activeOrganization.id,
                    });
                })
                .catch((error) => {
                    this._updateRefErrorObj(ref, error, errorObj);
                    throw errorObj;
                });
        }

        /**
         * This method is called with getExistingRefs() call information to return
         * a array of relative references from the passed reference.  An array is always returned.
         * It would be a empty array for a ref the doesn't have relative references or is not valid.
         * The ref is always a absolute reference starting with '/'.
         */
        pmGetExistingRefs(continuation, ref) {
            const errorObj = new JNIBError();
            if (arguments.length === 1) {
                ref = continuation;
                continuation = null;
            }

            return Promise.resolve()
                .then(() => {
                    return this._nibApiService.getExistingRefs({
                        ref: this._sanitizeRef(ref),
                        orgId: this._userSession.activeOrganization.id,
                    });
                })
                .catch((error) => {
                    this._updateRefErrorObj(ref, error, errorObj);
                    throw errorObj;
                });
        }

        /**
         * This method is called with getExistingRefInfos() call information to return
         * a array of relative references from the passed reference.  An array is always returned.
         * It would be a empty array for a ref the doesn't have relative references or is not valid.
         * The ref is always a absolute reference starting with '/'.
         */
        pmGetExistingRefInfos(continuation, ref) {
            const errorObj = new JNIBError();
            if (arguments.length === 1) {
                ref = continuation;
                continuation = null;
            }

            return Promise.resolve()
                .then(() => {
                    return this._nibApiService.getExistingRefInfos({
                        ref: this._sanitizeRef(ref),
                        orgId: this._userSession.activeOrganization.id,
                    });
                })
                .catch((error) => {
                    this._updateRefErrorObj(ref, error, errorObj);
                    throw errorObj;
                });
        }

        /**
         * This method is called with getRefContainer() call information to return
         * a object that has information about container the ref is from.
         * The ref is always a absolute reference starting with '/'.
         */
        pmGetRefContainer(continuation, ref) {
            const errorObj = new JNIBError();
            if (arguments.length === 1) {
                ref = continuation;
                continuation = null;
            }

            return Promise.resolve()
                .then(() => {
                    return this._nibApiService.getRefContainer({
                        ref: this._sanitizeRef(ref),
                        orgId: this._userSession.activeOrganization.id,
                    });
                })
                .catch((error) => {
                    this._updateRefErrorObj(ref, error, errorObj);
                    throw errorObj;
                });
        }

        /**
         * This method is called with require() call information to return
         * a load javascript code in the browser.
         * The ref is always a absolute reference starting with '/'.
         */
        pmRequire(continuation, ref) {
            const errorObj = new JNIBError();

            return new Promise((resolve, reject) => {
                let sT = document.createElement("script");

                sT.src = ref + "?orgId=" + this._userSession.activeOrganization.id;

                sT.onload = resolve;
                sT.onerror = reject;
                document.head.appendChild(sT);

                //Keep track of the JNIB Dom element for this continuation.
                continuation.addJNIBDomElement(ref, sT);
            }).catch((error) => {
                errorObj.setMessage("ref: " + ref + ", error: " + error);
                throw errorObj;
            });
        }

        /**
         * Storage Save Data
         */
        pmStorageSaveData(continuation, ref, data) {
            const errorObj = new JNIBError();
            const requestObj = _.merge({}, data, {
                ref: this._sanitizeRef(ref),
                orgId: this._userSession.activeOrganization.id,
            });
            return Promise.resolve()
                .then(() => {
                    return this._nibApiService.storageSaveData(requestObj);
                })
                .then((response) => {
                    //Note the JNIBEngineAPI is expecting the response to be a string so we get to convert this back into a string
                    if (_.isPlainObject(response)) {
                        return JSON.stringify(response);
                    } else {
                        return response;
                    }
                })
                .catch((error) => {
                    this._updateRefErrorObj(ref, error, errorObj);
                    throw errorObj;
                });
        }

        pmStorageDeleteData(continuation, ref, data) {
            const errorObj = new JNIBError();
            const requestObj = _.merge({}, data, {
                ref: this._sanitizeRef(ref),
                orgId: this._userSession.activeOrganization.id,
            });
            return Promise.resolve()
                .then(() => {
                    return this._nibApiService.storageDeleteData(requestObj);
                })
                .then((response) => {
                    //Note the JNIBEngineAPI is expecting the response to be a string so we get to convert this back into a string
                    if (_.isPlainObject(response)) {
                        return JSON.stringify(response);
                    } else {
                        return response;
                    }
                })
                .catch((error) => {
                    this._updateRefErrorObj(ref, error, errorObj);
                    throw errorObj;
                });
        }

        pmRunProvision(continuation, startTaskMsg, nodeRef, actionStruct, config) {
            let startTaskTime = continuation.logTaskStart(startTaskMsg);
            continuation.logs = [];
            return new Promise((resolve /*, reject*/) => {
                let request = {
                    nodeRef: nodeRef,
                    actionStruct: actionStruct,
                    orgId: this._userSession.activeOrganization.id,
                };
                this._nibApiService.runProvision(request, {
                    requestHandlers: {
                        Success: (response) => {
                            if (_.get(config, "finishObjLogs", false)) {
                                response.logs = continuation.logs;
                            }
                            if (response.errorMsg) {
                                console.error(response.errorMsg);
                                continuation.logTaskEndException(startTaskTime, startTaskMsg);
                                resolve(response);
                            } else {
                                continuation.logTaskEndNormal(startTaskTime, startTaskMsg);
                                resolve(response);
                            }
                        },
                        Progress: (data) => {
                            if (_.isArray(data.logs)) {
                                data.logs.forEach((log) => {
                                    continuation.logs.push(log);
                                    continuation.newLogEvent(log);
                                });
                            }
                        },
                        Failure: (response) => {
                            if (_.get(config, "finishObjLogs", false)) {
                                response.logs = continuation.logs;
                            }
                            console.error(response);
                            continuation.logTaskEndException(startTaskTime, startTaskMsg);
                            let errorResp = {
                                errorMsg: _.get(
                                    response,
                                    "errorMsg",
                                    _.get(response, "message", "Unknown Error")
                                ),
                                logs: response.logs,
                            };
                            resolve(errorResp);
                        },
                    },
                });
            });
        }
    };

module.exports = mixin;
